import { useState } from 'react';
import { Link, data } from 'react-router';
import type { Route } from './+types/event-detail';
import { apiFetch } from '~/lib/api-client';
import type { ApiResult, EventDto } from '~/lib/types';
import { AREA_LABELS, CATEGORY_LABELS } from '~/lib/labels';
import {
  googleCalUrl,
  linkedInShareUrl,
  outlookUrl,
  splitDateLabel,
  xShareUrl,
} from '~/lib/event-utils';
import { decideGate } from '~/lib/gate';
import { nativeShare } from '~/lib/native';
import { Directions } from '~/components/directions';
import { GateWall } from '~/components/account-gate';
import { SaveButton } from '~/components/save-button';

export function meta({ data }: Route.MetaArgs) {
  if (!data?.event) {
    return [{ title: 'Event — Tampa Bay Networking Calendar' }];
  }
  const e = data.event;
  const desc = e.description.length > 155 ? `${e.description.slice(0, 152)}…` : e.description;
  return [
    { title: `${e.title} · ${e.dateLabel} — Tampa Bay Networking Calendar` },
    { name: 'description', content: desc },
    { property: 'og:title', content: `${e.title} · ${e.dateLabel}` },
    { property: 'og:description', content: desc },
    { property: 'og:image', content: '/og-default.png' },
    { property: 'og:type', content: 'article' },
  ];
}

export async function loader({ request, params }: Route.LoaderArgs) {
  const res = await apiFetch(request, `/api/events/${params.slug}`);
  const body = (await res.json().catch(() => null)) as ApiResult<{ event: EventDto }> | null;
  if (!body || !body.ok) {
    throw new Response('Not Found', { status: 404 });
  }

  // Metered gate: crawlers + signed-in members always get the full page (SEO and
  // organizer distribution stay intact); anonymous humans get a few free views,
  // then the wall. Session is a cheap in-process check.
  const meRes = await apiFetch(request, '/api/me');
  const loggedIn =
    meRes.ok &&
    (((await meRes.json().catch(() => null)) as ApiResult<unknown> | null)?.ok ?? false);
  const gate = decideGate({
    slug: params.slug,
    ua: request.headers.get('user-agent') ?? '',
    loggedIn,
    cookieHeader: request.headers.get('cookie'),
  });

  // Saved state (members only) so the Save button renders correctly during SSR.
  let saved = false;
  if (loggedIn) {
    const sRes = await apiFetch(request, `/api/me/saves/${body.data.event.id}`);
    if (sRes.ok) {
      const sb = (await sRes.json().catch(() => null)) as ApiResult<{ saved: boolean }> | null;
      saved = sb?.ok ? sb.data.saved : false;
    }
  }

  const url = new URL(request.url);
  return data(
    {
      event: body.data.event,
      url: request.url,
      path: url.pathname,
      gated: gate.gated,
      loggedIn,
      saved,
    },
    gate.setCookie ? { headers: { 'Set-Cookie': gate.setCookie } } : undefined
  );
}

/** First ~180 chars of the description as the pre-wall teaser. */
function teaser(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > 180 ? `${clean.slice(0, 180)}…` : clean;
}

function buildJsonLd(e: EventDto, pageUrl: string): string {
  const priceMatch = e.cost.match(/\d+(?:\.\d{2})?/);
  const ld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: e.title,
    startDate: e.iso,
    eventStatus:
      e.status === 'cancelled'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
    eventAttendanceMode:
      e.area === 'online'
        ? 'https://schema.org/OnlineEventAttendanceMode'
        : 'https://schema.org/OfflineEventAttendanceMode',
    description: e.description,
    url: pageUrl,
    location:
      e.area === 'online'
        ? { '@type': 'VirtualLocation', url: e.registerUrl || pageUrl }
        : {
            '@type': 'Place',
            name: e.locationName,
            address: e.address || e.locationName,
          },
  };
  if (e.endsAt) {
    // endDate shares the start date's offset — close enough for schema purposes
    ld.endDate = new Date(e.endsAt * 1000).toISOString();
  }
  if (e.organizerName) {
    ld.organizer = {
      '@type': 'Organization',
      name: e.organizerName,
      ...(e.organizerWebsite ? { url: e.organizerWebsite } : {}),
    };
  }
  if (e.isFree) {
    ld.isAccessibleForFree = true;
    ld.offers = { '@type': 'Offer', price: 0, priceCurrency: 'USD', url: e.registerUrl || pageUrl };
  } else if (priceMatch) {
    ld.offers = {
      '@type': 'Offer',
      price: Number(priceMatch[0]),
      priceCurrency: 'USD',
      url: e.registerUrl || pageUrl,
    };
  }
  return JSON.stringify(ld);
}

export default function EventDetail({ loaderData }: Route.ComponentProps) {
  const { event: e, url, path, gated, loggedIn, saved } = loaderData;
  const chip = splitDateLabel(e.dateLabel);
  const [copied, setCopied] = useState(false);
  const cancelled = e.status === 'cancelled';

  async function copyLink() {
    // In the app this opens the native share sheet; on the web it copies the link.
    if (await nativeShare({ title: e.title, text: `${e.title} · ${e.dateLabel}`, url })) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — no-op
    }
  }

  return (
    <main className="page">
      <div className="container page-narrow">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildJsonLd(e, url) }}
        />

        <nav aria-label="Breadcrumb" className="small detail-breadcrumb">
          <Link to="/">← Back to the calendar</Link>
        </nav>

        {cancelled && (
          <div className="notice notice-error" role="alert">
            <strong>This event has been cancelled.</strong> Check with the organizer for rescheduled
            dates.
          </div>
        )}

        <article className="stack">
          <header className="detail-header">
            <div className="date-chip" aria-hidden="true">
              <span className="date-chip-dow">{chip.dow}</span>
              <span className="date-chip-day">{chip.day}</span>
              <span className="date-chip-mon">{chip.mon}</span>
            </div>
            <div>
              <h1>{e.title}</h1>
              <p className="muted">
                Hosted by{' '}
                {e.organizerWebsite ? (
                  <a href={e.organizerWebsite} target="_blank" rel="noopener noreferrer">
                    {e.organizerName}
                  </a>
                ) : (
                  <strong>{e.organizerName}</strong>
                )}
              </p>
            </div>
          </header>

          <ul className="fact-list card">
            <li>
              <span className="fact-label">When</span>
              <span>
                <time dateTime={e.iso} className="t-num">
                  {e.dateLabel} · {e.timeLabel}
                  {e.endTimeLabel ? ` – ${e.endTimeLabel}` : ''}
                </time>{' '}
                <span className="muted">ET</span>
              </span>
            </li>
            <li>
              <span className="fact-label">Where</span>
              <span>
                <strong>{e.locationName}</strong>
                {e.address && <span className="muted"> · {e.address}</span>}
                {(e.locationName || e.address) && e.area !== 'online' && (
                  <Directions locationName={e.locationName} address={e.address} />
                )}
              </span>
            </li>
            <li>
              <span className="fact-label">Cost</span>
              <span>
                {e.isFree ? <span className="tag tag-free">Free</span> : e.cost || 'Paid'}
              </span>
            </li>
            <li>
              <span className="fact-label">Type</span>
              <span>
                <span className="tag">{CATEGORY_LABELS[e.category]}</span>{' '}
                <span className="tag">{AREA_LABELS[e.area]}</span>
              </span>
            </li>
          </ul>

          {gated ? (
            <>
              <div className="detail-description">
                <p>{teaser(e.description)}</p>
              </div>
              <GateWall next={path} />
            </>
          ) : (
            <>
              <div className="detail-actions-row">
                <SaveButton eventId={e.id} initialSaved={saved} loggedIn={loggedIn} next={path} />
              </div>

              <div className="detail-description">
                {e.description.split(/\n{2,}/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              {!cancelled && e.registerUrl && (
                <p>
                  <a
                    href={e.registerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ui-btn btn-primary btn-lg"
                  >
                    Register →
                  </a>
                </p>
              )}

              <section aria-label="Add to calendar">
                <h2 className="detail-section-head">Add to calendar</h2>
                <div className="row">
                  <a
                    href={googleCalUrl(e)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ui-btn btn-secondary btn-sm"
                  >
                    Google
                  </a>
                  <a href={`/events/${e.slug}.ics`} className="ui-btn btn-secondary btn-sm">
                    Apple / .ics
                  </a>
                  <a
                    href={outlookUrl(e)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ui-btn btn-secondary btn-sm"
                  >
                    Outlook
                  </a>
                </div>
              </section>

              <section aria-label="Share">
                <h2 className="detail-section-head">Share</h2>
                <div className="row">
                  <button type="button" className="ui-btn btn-secondary btn-sm" onClick={copyLink}>
                    {copied ? 'Copied ✓' : 'Copy link'}
                  </button>
                  <a
                    href={linkedInShareUrl(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ui-btn btn-secondary btn-sm"
                  >
                    LinkedIn
                  </a>
                  <a
                    href={xShareUrl(url, `${e.title} · ${e.dateLabel}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ui-btn btn-secondary btn-sm"
                  >
                    X
                  </a>
                </div>
              </section>

              {e.seriesId && (
                <p className="notice">
                  <span>
                    <strong>Part of a series.</strong>{' '}
                    <Link to={`/events?q=${encodeURIComponent(e.title)}`}>
                      See other dates for this event →
                    </Link>
                  </span>
                </p>
              )}
            </>
          )}
        </article>
      </div>
    </main>
  );
}
